import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import {
  Brain, RefreshCw, Award, Zap, DollarSign, Database,
  Trophy, Sparkles, Unlock, Eye, Ear, Video, Image as ImageIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface Benchmarks {
  mmlu_pro: number
  gpqa_diamond: number
  humaneval: number
  swe_bench_verified: number
  math: number
  ifeval: number
}

interface Model {
  id: string
  name: string
  provider: string
  released: string
  context_window: number
  max_output: number
  price_input: number
  price_output: number
  speed_tps: number
  modality: string[]
  open_weights: boolean
  benchmarks: Benchmarks
  strengths: string
  composite: number
  value_score: number
  blended_price: number
}

interface BenchmarkMeta { key: keyof Benchmarks; label: string; description: string }

interface Leaders {
  benchmarks: Record<string, { id: string; name: string; provider: string; score: number }>
  categories: {
    overall: { id: string; name: string; provider: string; score: number }
    best_value: { id: string; name: string; provider: string; value_score: number }
    cheapest: { id: string; name: string; provider: string; blended_price: number }
    fastest: { id: string; name: string; provider: string; tps: number }
    largest_context: { id: string; name: string; provider: string; context_window: number }
    best_open_weights: { id: string; name: string; provider: string; score: number } | null
  }
}

type SortKey = 'composite' | 'price' | 'speed' | 'context' | 'released'

// ── Helpers ──────────────────────────────────────────────────────────────────

const PROVIDER_COLOR: Record<string, string> = {
  Anthropic: '#d97757',
  OpenAI: '#10a37f',
  Google: '#4285f4',
  Meta: '#1877f2',
  Mistral: '#ff7000',
  DeepSeek: '#6466f1',
  xAI: '#1da1f2',
  Alibaba: '#ff6a00',
}

const MODALITY_ICON: Record<string, React.ElementType> = {
  text: Brain,
  vision: ImageIcon,
  audio: Ear,
  video: Video,
}

function formatContext(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return n.toString()
}

function benchColor(score: number): string {
  if (score >= 85) return 'bg-emerald'
  if (score >= 75) return 'bg-blue'
  if (score >= 65) return 'bg-amber'
  return 'bg-text-muted'
}

// ── Leader Card ──────────────────────────────────────────────────────────────

function LeaderCard({
  title, icon: Icon, name, provider, metric, accent,
}: {
  title: string
  icon: React.ElementType
  name: string
  provider: string
  metric: string
  accent: string
}) {
  return (
    <div className="card p-3 flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <Icon size={12} className={accent} />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
          {title}
        </span>
      </div>
      <div className="text-sm font-bold text-text-primary leading-tight">{name}</div>
      <div className="flex items-center justify-between">
        <span
          className="text-[10px] font-medium"
          style={{ color: PROVIDER_COLOR[provider] || '#999' }}
        >
          {provider}
        </span>
        <span className={clsx('text-[11px] font-mono font-bold', accent)}>{metric}</span>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AIModelsPage() {
  const [models, setModels] = useState<Model[]>([])
  const [benchmarks, setBenchmarks] = useState<BenchmarkMeta[]>([])
  const [providers, setProviders] = useState<string[]>([])
  const [leaders, setLeaders] = useState<Leaders | null>(null)
  const [loading, setLoading] = useState(true)
  const [provider, setProvider] = useState<string>('')
  const [openOnly, setOpenOnly] = useState(false)
  const [sort, setSort] = useState<SortKey>('composite')
  const [expanded, setExpanded] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | boolean> = { sort }
      if (provider) params.provider = provider
      if (openOnly) params.open_only = true
      const [{ data }, { data: L }] = await Promise.all([
        api.get('/api/ai-models', { params }),
        api.get('/api/ai-models/leaders'),
      ])
      setModels(data.models)
      setBenchmarks(data.benchmarks)
      setProviders(data.providers)
      setLeaders(L)
    } finally {
      setLoading(false)
    }
  }, [provider, openOnly, sort])

  useEffect(() => { fetchData() }, [fetchData])

  const maxComposite = useMemo(
    () => models.reduce((m, r) => Math.max(m, r.composite), 0) || 1,
    [models]
  )

  if (loading && models.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Brain size={20} className="text-violet" /> AI Model Leaderboard
          </h1>
          <p className="text-[10px] text-text-secondary">
            {models.length} frontier LLMs — benchmarks, pricing, context, speed compared side-by-side
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          className="btn-ghost text-[11px] flex items-center gap-1.5"
          title="Refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Category leaders */}
      {leaders && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <LeaderCard
            title="Overall"
            icon={Trophy}
            name={leaders.categories.overall.name}
            provider={leaders.categories.overall.provider}
            metric={`${leaders.categories.overall.score}`}
            accent="text-amber"
          />
          <LeaderCard
            title="Best Value"
            icon={Sparkles}
            name={leaders.categories.best_value.name}
            provider={leaders.categories.best_value.provider}
            metric={`${leaders.categories.best_value.value_score} vs$`}
            accent="text-emerald"
          />
          <LeaderCard
            title="Cheapest"
            icon={DollarSign}
            name={leaders.categories.cheapest.name}
            provider={leaders.categories.cheapest.provider}
            metric={`$${leaders.categories.cheapest.blended_price}/M`}
            accent="text-cyan"
          />
          <LeaderCard
            title="Fastest"
            icon={Zap}
            name={leaders.categories.fastest.name}
            provider={leaders.categories.fastest.provider}
            metric={`${leaders.categories.fastest.tps} tps`}
            accent="text-orange"
          />
          <LeaderCard
            title="Largest Ctx"
            icon={Database}
            name={leaders.categories.largest_context.name}
            provider={leaders.categories.largest_context.provider}
            metric={formatContext(leaders.categories.largest_context.context_window)}
            accent="text-blue"
          />
          {leaders.categories.best_open_weights && (
            <LeaderCard
              title="Best Open"
              icon={Unlock}
              name={leaders.categories.best_open_weights.name}
              provider={leaders.categories.best_open_weights.provider}
              metric={`${leaders.categories.best_open_weights.score}`}
              accent="text-violet"
            />
          )}
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Provider</span>
          <select
            value={provider}
            onChange={e => setProvider(e.target.value)}
            className="input py-1 text-[11px] pr-6"
            title="Provider"
          >
            <option value="">All</option>
            {providers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Sort</span>
          <select
            value={sort}
            onChange={e => setSort(e.target.value as SortKey)}
            className="input py-1 text-[11px] pr-6"
            title="Sort"
          >
            <option value="composite">Composite score</option>
            <option value="price">Price (low → high)</option>
            <option value="speed">Speed (high → low)</option>
            <option value="context">Context window</option>
            <option value="released">Released (newest)</option>
          </select>
        </div>
        <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-text-secondary">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={e => setOpenOnly(e.target.checked)}
            className="accent-violet"
          />
          Open-weights only
        </label>
        <span className="text-[10px] text-text-muted ml-auto">
          Composite = weighted benchmark average (SWE-bench + GPQA weighted highest)
        </span>
      </div>

      {/* Leaderboard table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-raised/50">
              <tr className="border-b border-border">
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-8">#</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Model</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Provider</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Composite</th>
                {benchmarks.map(b => (
                  <th
                    key={b.key}
                    className="text-[10px] text-text-muted font-semibold py-2 px-2 text-right"
                    title={b.description}
                  >
                    {b.label}
                  </th>
                ))}
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Ctx</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">In $/M</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Out $/M</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Speed</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Modality</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m, i) => {
                const isExpanded = expanded === m.id
                return (
                  <Fragment key={m.id}>
                    <tr
                      onClick={() => setExpanded(isExpanded ? null : m.id)}
                      className={clsx(
                        'border-b border-border/50 hover:bg-surface-raised/50 transition-colors cursor-pointer',
                        isExpanded && 'bg-surface-raised/50'
                      )}
                    >
                      <td className="py-2 px-3 text-[11px] font-mono text-text-muted">
                        {i + 1}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-semibold text-text-primary">{m.name}</span>
                          {m.open_weights && (
                            <Unlock size={10} className="text-violet" aria-label="Open weights" />
                          )}
                        </div>
                        <div className="text-[9px] text-text-muted">Released {m.released}</div>
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className="text-[10px] font-semibold"
                          style={{ color: PROVIDER_COLOR[m.provider] || '#999' }}
                        >
                          {m.provider}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <div className="w-12 h-1.5 bg-surface-raised rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue to-emerald"
                              style={{ width: `${(m.composite / maxComposite) * 100}%` }}
                            />
                          </div>
                          <span className="text-[11px] font-mono font-bold text-text-primary w-10 text-right">
                            {m.composite.toFixed(1)}
                          </span>
                        </div>
                      </td>
                      {benchmarks.map(b => {
                        const v = m.benchmarks[b.key]
                        return (
                          <td key={b.key} className="py-2 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <div className="w-8 h-1 bg-surface-raised rounded-full overflow-hidden">
                                <div className={clsx('h-full', benchColor(v))} style={{ width: `${v}%` }} />
                              </div>
                              <span className="text-[10px] font-mono text-text-secondary w-8 text-right">
                                {v.toFixed(1)}
                              </span>
                            </div>
                          </td>
                        )
                      })}
                      <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                        {formatContext(m.context_window)}
                      </td>
                      <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                        ${m.price_input.toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                        ${m.price_output.toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                        {m.speed_tps}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          {m.modality.map(mod => {
                            const Icon = MODALITY_ICON[mod] || Brain
                            return (
                              <Icon
                                key={mod}
                                size={11}
                                className="text-text-muted"
                                aria-label={mod}
                              />
                            )
                          })}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-surface-raised/30">
                        <td colSpan={12} className="px-6 py-3 border-b border-border/50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[11px]">
                            <div>
                              <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">
                                Strengths
                              </div>
                              <div className="text-text-secondary">{m.strengths}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">
                                Value score
                              </div>
                              <div className="text-text-secondary font-mono">
                                {m.value_score} points per $1/M blended
                              </div>
                              <div className="text-[10px] text-text-muted mt-1">
                                Blended price: ${m.blended_price}/M tokens (weighted 1:3 in:out)
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">
                                Capabilities
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {m.modality.map(mod => (
                                  <span
                                    key={mod}
                                    className="px-1.5 py-0.5 bg-surface-raised rounded text-[10px] text-text-secondary capitalize"
                                  >
                                    {mod}
                                  </span>
                                ))}
                                {m.open_weights && (
                                  <span className="px-1.5 py-0.5 bg-violet/10 text-violet rounded text-[10px]">
                                    Open weights
                                  </span>
                                )}
                                <span className="px-1.5 py-0.5 bg-surface-raised rounded text-[10px] text-text-secondary">
                                  Max output: {formatContext(m.max_output)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Benchmark legend */}
      <div className="card p-3">
        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2 flex items-center gap-1.5">
          <Award size={11} /> Benchmark legend
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
          {benchmarks.map(b => (
            <div key={b.key} className="flex items-baseline gap-2 text-[10px]">
              <span className="font-semibold text-text-primary min-w-[64px]">{b.label}</span>
              <span className="text-text-muted">{b.description}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10px] text-text-muted">
          <Eye size={10} className="inline mr-1" /> Click any row for details. Scores are from public reports
          (lmarena, artificialanalysis.ai, provider disclosures) — independent evals where available.
        </div>
      </div>
    </div>
  )
}
